import React, { useLayoutEffect, useRef, useState } from "react";
import styled from "styled-components";
import { handoutGrayFocusAssetUrl } from "../../features/handoutWordLayout";

export const getHandoutFocusFrameUrl = (frame) =>
  typeof frame?.url === "string" ? frame.url.trim() : "";

// Match the cloud artwork's white-margin crop in the Word exporter. Keep the
// original URL and pixels; the background alone stretches with native content.
export const getHandoutBackgroundCrop = (url) => {
  let path = String(url || "");
  try { path = decodeURIComponent(new URL(path).pathname); } catch { /* Literal/data URL. */ }
  if (path.endsWith("/觀念底_淺灰紋理_左上斜角.png")) return { width: 966, height: 610, left: 1, top: 1, cropWidth: 964, cropHeight: 609 };
  if (path.endsWith("/重點外框_雙線斜角_橫式_2284x1749.png") || path.endsWith("/數學講義元素物件設計-07.png")) {
    return { width: 2284, height: 1749, left: 136, top: 168, cropWidth: 2011, cropHeight: 1381 };
  }
  return { width: 1, height: 1, left: 0, top: 0, cropWidth: 1, cropHeight: 1 };
};

export default function HandoutFocusFrame({ frame, grayAsset, backgroundStyle = "frame", children }) {
  const url = backgroundStyle === "gray" ? getHandoutFocusFrameUrl(grayAsset) || handoutGrayFocusAssetUrl : getHandoutFocusFrameUrl(frame);
  const frameRef = useRef(null);
  const [fragments, setFragments] = useState([]);
  useLayoutEffect(() => {
    const frame = frameRef.current;
    if (!frame || !url) return undefined;
    let pending = 0, stopped = false;
    const measure = () => {
      pending = 0;
      if (stopped) return;
      const rects = Array.from(frame.getClientRects());
      const width = parseFloat(getComputedStyle(frame).width);
      const scale = rects[0]?.width / width;
      const local = (value) => Math.round(value / scale * 100) / 100;
      const next = rects.length > 1 && scale > 0 ? rects.map((rect) => ({
        left: local(rect.left - rects[0].left), top: local(rect.top - rects[0].top),
        width: local(rect.width), height: local(rect.height),
      })) : [];
      setFragments((current) => JSON.stringify(current) === JSON.stringify(next) ? current : next);
    };
    const schedule = () => { if (!pending && !stopped) pending = requestAnimationFrame(measure); };
    // Earlier content or delayed figures can change this block's page breaks.
    const flow = frame.closest('[data-handout-flow]') || frame;
    const resize = typeof ResizeObserver === "function" ? new ResizeObserver(schedule) : null;
    resize?.observe(frame); resize?.observe(frame.firstElementChild);
    // A subsection can move to another column without changing the frame's
    // overall size. Watch its geometry too, especially after Python rendering.
    frame.querySelectorAll('[data-focus-subsection], [data-focus-image-id]').forEach((node) => resize?.observe(node));
    const changes = new MutationObserver(schedule);
    changes.observe(flow, { childList: true, subtree: true, characterData: true, attributes: true,
      attributeFilter: ["src", "width", "height", "style", "class", "data-handout-image-size"] });
    flow.addEventListener("load", schedule, true);
    window.addEventListener("resize", schedule);
    document.fonts?.addEventListener?.("loadingdone", schedule);
    document.fonts?.ready?.then(schedule);
    measure();
    return () => { stopped = true; cancelAnimationFrame(pending); resize?.disconnect(); changes.disconnect();
      flow.removeEventListener("load", schedule, true); window.removeEventListener("resize", schedule);
      document.fonts?.removeEventListener?.("loadingdone", schedule); };
  }, [url]);
  if (!url) return <>{children}</>;
  const crop = getHandoutBackgroundCrop(url);
  // Artwork and editable content are separate siblings. The artwork never
  // contributes height; its bounds follow the content, including page fragments.
  const layers = fragments.length ? fragments : [{ inset: 0 }];
  return <FocusFrame ref={frameRef} data-focus-background={backgroundStyle} data-focus-frame={url}>
    <FrameContent data-focus-foreground>{children}</FrameContent>
    {layers.map((fragment, index) => <FragmentBackground key={index} aria-hidden="true" data-focus-background-fragment style={fragment}>
      <img src={url} alt="" draggable={false} style={{
        left: `${-crop.left / crop.cropWidth * 100}%`, top: `${-crop.top / crop.cropHeight * 100}%`,
        width: `${crop.width / crop.cropWidth * 100}%`, height: `${crop.height / crop.cropHeight * 100}%`,
      }} />
    </FragmentBackground>)}
  </FocusFrame>;
}

export function TexturedGrayFocusFrame({ children }) {
  return <HandoutFocusFrame backgroundStyle="gray">{children}</HandoutFocusFrame>;
}

const FocusFrame = styled.div`
  position: relative;
  isolation: isolate;
  display: flow-root;
  box-sizing: border-box;
  width: 100%;
  min-width: 0;
  padding: 18pt 14pt;
  color: inherit;
  background-repeat: no-repeat;
  -webkit-box-decoration-break: clone;
  box-decoration-break: clone;
`;
const FrameContent = styled.div`
  min-width: 0;
`;

const FragmentBackground = styled.div`
  position: absolute;
  overflow: hidden;
  z-index: -1;
  pointer-events: none;
  img { position: absolute; display: block; max-width: none; max-height: none; object-fit: fill; }
`;
